import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Package } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import { getDashboardProductImageSrc } from "@/lib/productImageUrl";

interface ProductPreviewProps {
	product: {
		name: string;
		image?: string;
		type: "product" | "service" | "menu_item" | "combo";
		unit: "piece" | "bulk" | "weight";
		sellingPrice: number;
		isActive?: boolean;
		status?: "active" | "inactive" | "out_of_stock" | "discontinued";
	};
	categoryName?: string;
	fromPrice?: boolean;
}

const typeStyles = {
	product: { key: "product", tone: "info" },
	service: { key: "service", tone: "violet" },
	menu_item: { key: "menuItem", tone: "orange" },
	combo: { key: "combo", tone: "violet" },
} as const;

/** A read-only view of the current draft; saving remains the editor's responsibility. */
export function ProductPreview({ product, categoryName, fromPrice = false }: ProductPreviewProps) {
	const { t } = useTranslation();
	const currency = useCurrency();
	const imageSrc = getDashboardProductImageSrc(product.image);
	const [failedImage, setFailedImage] = useState<string>();
	const type = typeStyles[product.type];
	const status =
		product.status ??
		(typeof product.isActive === "boolean" ? (product.isActive ? "active" : "inactive") : undefined);
	const statusKey = status === "out_of_stock" ? "outOfStock" : status;
	return (
		<section
			className="product-live-preview"
			aria-labelledby="product-preview-heading"
			data-testid="product-preview"
		>
			<h2 id="product-preview-heading" className="text-sm font-semibold text-muted-foreground">
				{t("productEditor.preview.title")}
			</h2>
			<div className="product-preview-body">
				<div className="product-preview-image">
					{imageSrc && imageSrc !== failedImage ? (
						<img
							src={imageSrc}
							alt={product.name || t("products.form.name")}
							onError={() => setFailedImage(imageSrc)}
						/>
					) : (
						<div className="flex flex-col items-center gap-2 text-muted-foreground">
							<Package className="h-10 w-10" aria-hidden="true" strokeWidth={1.25} />
							<span className="text-xs">{t("productEditor.preview.noImage")}</span>
						</div>
					)}
				</div>
				<div className="product-preview-info">
					<div className="flex flex-wrap gap-2">
						<Badge variant={type.tone}>{t(`products.types.${type.key}`)}</Badge>
						{statusKey && (
							<Badge
								variant={
									status === "active"
										? "success"
										: status === "out_of_stock"
											? "warning"
											: "secondary"
								}
							>
								{t(`products.filters.${statusKey}`)}
							</Badge>
						)}
					</div>
					<h3 className="product-preview-name" data-testid="product-preview-name" dir="auto">
						{product.name.trim() || t("products.form.name")}
					</h3>
					{categoryName && (
						<p className="text-sm text-muted-foreground" dir="auto" data-testid="product-preview-category">
							{categoryName}
						</p>
					)}
					<dl className="product-preview-price">
						<div>
							<dt className="text-xs text-muted-foreground">
								{t(fromPrice ? "productSetup.fromPrice" : "products.form.sellingPrice")}
							</dt>
							<dd className="mt-1 text-2xl font-bold tabular-nums" data-testid="product-preview-price">
								<Price value={product.sellingPrice} currency={currency} />
							</dd>
						</div>
						<div className="text-sm">
							<dt className="text-xs text-muted-foreground">{t("products.form.unit")}</dt>
							<dd className="mt-1 font-medium">{t(`products.units.${product.unit}`)}</dd>
						</div>
					</dl>
				</div>
			</div>
		</section>
	);
}
